'use client';

import React from 'react';
import Image from 'next/image';
import {
  Gauge,
  Phone,
  Mail,
  MapPin,
  Facebook,
  Instagram,
  Twitter,
  Youtube
} from 'lucide-react';
import { ActivePageView } from '@/types';
import { useLocale } from '@/lib/i18n';
import { usePublicBrand } from '@/components/site/PublicBrandProvider';
import type { DesignId } from '@/lib/storefront/designs';

interface FooterProps {
  onNavigate: (view: ActivePageView) => void;
  social?: { facebook: string; instagram: string; x: string; youtube: string };
  /** Chosen in the appearance editor; the editorial design gets a light footer. */
  design?: DesignId;
}

/** Per-design footer surface. The columns themselves are shared. */
const FOOTER_CHROME: Record<DesignId, { root: string; divider: string; tile: string; heading: string; body: string; bottom: string }> = {
  classic: {
    root: 'bg-[var(--color-secondary)] text-slate-400 border-t border-slate-800/80',
    divider: 'border-slate-800/60',
    tile: 'bg-slate-900 border border-slate-700',
    heading: 'text-white',
    body: 'text-slate-400',
    bottom: 'text-slate-500',
  },
  editorial: {
    root: 'bg-[#F3EBE0] text-slate-600 border-t border-[#e3d7c6]',
    divider: 'border-[#e3d7c6]',
    tile: 'bg-white border border-[#e3d7c6]',
    heading: 'text-slate-900',
    body: 'text-slate-600',
    bottom: 'text-slate-500',
  },
  roadtrip: {
    root: 'bg-[var(--color-secondary)] text-slate-400 border-t border-white/10',
    divider: 'border-white/10',
    tile: 'bg-slate-900 border border-slate-700',
    heading: 'text-white',
    body: 'text-slate-400',
    bottom: 'text-slate-500',
  },
};

export function Footer({ onNavigate, social, design = 'classic' }: FooterProps) {
  const { t } = useLocale();
  const brand = usePublicBrand();
  const chrome = FOOTER_CHROME[design] ?? FOOTER_CHROME.classic;
  return (
    <footer className={`w-full pt-16 pb-8 ${chrome.root}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-8 pb-12 border-b ${chrome.divider}`}>
          {/* Column 1: Logo & Mission & Socials (Span 4) */}
          <div className="lg:col-span-4 flex flex-col items-start">
            <button
              type="button"
              onClick={() => onNavigate('home')}
              className="flex items-center gap-2.5 text-left cursor-pointer group mb-4"
            >
              <div className={`relative w-10 h-10 rounded-full ${chrome.tile} flex items-center justify-center shadow-sm group-hover:scale-105 transition-transform overflow-hidden`}>
                {brand.logoUrl ? (
                  <Image src={brand.logoUrl} alt="" width={40} height={40} unoptimized className="h-full w-full object-cover" />
                ) : (
                  <Gauge className="w-6 h-6 text-[var(--color-primary)]" />
                )}
              </div>
              <div className="flex flex-col">
                <div className={`text-2xl font-black tracking-tight leading-none ${chrome.heading} font-sans`}>
                  {brand.shortName || brand.name}
                </div>
                <span className="text-[9px] tracking-[0.25em] font-bold text-slate-400 uppercase mt-0.5">
                  CAR RENTAL
                </span>
              </div>
            </button>

            <p className={`text-xs sm:text-sm ${chrome.body} leading-relaxed max-w-sm mb-6`}>
              {t('foot.tagline')}
            </p>

            {/* Social Icons */}
            <div className="flex items-center gap-3">
              {social?.facebook ? <a href={social.facebook} aria-label="Facebook" className={`w-8 h-8 rounded-full ${chrome.tile} hover:border-amber-500 hover:text-amber-400 flex items-center justify-center ${chrome.body} transition-colors`}><Facebook className="w-4 h-4" /></a> : null}
              {social?.instagram ? <a href={social.instagram} aria-label="Instagram" className={`w-8 h-8 rounded-full ${chrome.tile} hover:border-amber-500 hover:text-amber-400 flex items-center justify-center ${chrome.body} transition-colors`}><Instagram className="w-4 h-4" /></a> : null}
              {social?.x ? <a href={social.x} aria-label="X" className={`w-8 h-8 rounded-full ${chrome.tile} hover:border-amber-500 hover:text-amber-400 flex items-center justify-center ${chrome.body} transition-colors`}><Twitter className="w-4 h-4" /></a> : null}
              {social?.youtube ? <a href={social.youtube} aria-label="YouTube" className={`w-8 h-8 rounded-full ${chrome.tile} hover:border-amber-500 hover:text-amber-400 flex items-center justify-center ${chrome.body} transition-colors`}><Youtube className="w-4 h-4" /></a> : null}
            </div>
          </div>

          {/* Column 2: COMPANY (Span 2) */}
          <div className="lg:col-span-2">
            <h4 className={`text-xs font-black tracking-wider uppercase ${chrome.heading} mb-4`}>
              {t('foot.company')}
            </h4>
            <ul className={`space-y-2.5 text-xs ${chrome.body}`}>
              <li>
                <button
                  type="button"
                  onClick={() => onNavigate('about')}
                  className="hover:text-amber-400 transition-colors cursor-pointer text-left"
                >
                  {t('foot.about')}
                </button>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => onNavigate('about')}
                  className="hover:text-amber-400 transition-colors cursor-pointer text-left"
                >
                  {t('foot.careers')}
                </button>
              </li>
              <li>
                <button type="button" onClick={() => onNavigate('terms')} className="hover:text-amber-400 transition-colors cursor-pointer text-left">
                  {t('foot.terms')}
                </button>
              </li>
              <li>
                <button type="button" onClick={() => onNavigate('privacy')} className="hover:text-amber-400 transition-colors cursor-pointer text-left">
                  {t('foot.privacy')}
                </button>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => onNavigate('contact')}
                  className="hover:text-amber-400 transition-colors cursor-pointer text-left"
                >
                  {t('foot.contact')}
                </button>
              </li>
            </ul>
          </div>

          {/* Column 3: INFORMATION (Span 2) */}
          <div className="lg:col-span-2">
            <h4 className={`text-xs font-black tracking-wider uppercase ${chrome.heading} mb-4`}>
              {t('foot.info')}
            </h4>
            <ul className={`space-y-2.5 text-xs ${chrome.body}`}>
              <li>
                <button
                  type="button"
                  onClick={() => onNavigate('faq')}
                  className="hover:text-amber-400 transition-colors cursor-pointer text-left"
                >
                  {t('foot.faq')}
                </button>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => onNavigate('services')}
                  className="hover:text-amber-400 transition-colors cursor-pointer text-left"
                >
                  {t('foot.guide')}
                </button>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => onNavigate('services')}
                  className="hover:text-amber-400 transition-colors cursor-pointer text-left"
                >
                  {t('foot.insurance')}
                </button>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => onNavigate('services')}
                  className="hover:text-amber-400 transition-colors cursor-pointer text-left"
                >
                  {t('foot.payment')}
                </button>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => onNavigate('about')}
                  className="hover:text-amber-400 transition-colors cursor-pointer text-left"
                >
                  {t('foot.blog')}
                </button>
              </li>
            </ul>
          </div>

          {/* Column 4: TOP LOCATIONS (Span 2) */}
          <div className="lg:col-span-2">
            <h4 className={`text-xs font-black tracking-wider uppercase ${chrome.heading} mb-4`}>
              {t('foot.locations')}
            </h4>
            <ul className={`space-y-2.5 text-xs ${chrome.body}`}>
              <li>
                <button
                  type="button"
                  onClick={() => onNavigate('locations')}
                  className="hover:text-amber-400 transition-colors cursor-pointer text-left"
                >
                  Los Angeles
                </button>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => onNavigate('locations')}
                  className="hover:text-amber-400 transition-colors cursor-pointer text-left"
                >
                  New York
                </button>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => onNavigate('locations')}
                  className="hover:text-amber-400 transition-colors cursor-pointer text-left"
                >
                  Miami
                </button>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => onNavigate('locations')}
                  className="hover:text-amber-400 transition-colors cursor-pointer text-left"
                >
                  Chicago
                </button>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => onNavigate('locations')}
                  className="hover:text-amber-400 transition-colors cursor-pointer text-left"
                >
                  Orlando
                </button>
              </li>
            </ul>
          </div>

          {/* Column 5: CONTACT US (Span 2) */}
          <div className="lg:col-span-2">
            <h4 className={`text-xs font-black tracking-wider uppercase ${chrome.heading} mb-4`}>
              {t('foot.contactUs')}
            </h4>
            <ul className={`space-y-3 text-xs ${chrome.body}`}>
              <li className="flex items-center gap-2">
                <Phone className="w-3.5 h-3.5 text-[var(--color-primary)] shrink-0" />
                <a href={brand.phone ? `tel:${brand.phone.replace(/\s/g, '')}` : undefined} className="hover:text-amber-500 transition-colors">
                  {brand.phone || '—'}
                </a>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="w-3.5 h-3.5 text-[var(--color-primary)] shrink-0" />
                <a href={brand.email ? `mailto:${brand.email}` : undefined} className="hover:text-amber-500 transition-colors">
                  {brand.email || '—'}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="w-3.5 h-3.5 text-[var(--color-primary)] shrink-0 mt-0.5" />
                <span>{[brand.address, brand.city].filter(Boolean).join(', ') || '—'}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar: Copyright & Slogan */}
        <div className={`pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs ${chrome.bottom}`}>
          <p>{t('foot.rights')}</p>
          <p className="italic">{t('foot.slogan')}</p>
        </div>
      </div>
    </footer>
  );
}
